import { PrintService } from "./print.service";
import { GlobalService } from "app/global.service";
import { Component, HostListener, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { Subject, Subscription, take, takeUntil } from "rxjs";
import { FuseConfigService } from "@fuse/services/config.service";
import { trigger, transition, style, animate } from "@angular/animations";
import { TopNotificationService } from "app/layout/components/top-notification/top-notification.service";
import { 
  FusePerfectScrollbarDirective,
} from "@fuse/directives/fuse-perfect-scrollbar/fuse-perfect-scrollbar.directive";
import { LayoutService } from "app/layout/layout.service";
import { Router } from "@angular/router";
import { PopupDialogQueueService } from "app/popup-dialog-queue.service";
import { MatDialog } from '@angular/material/dialog';
import { ViewportService } from "app/core/services/viewport.service";
import { EventStreamService } from "app/main/plus/calendar/event-stream.service";
import { BulkSchedulingStateService } from "app/main/plus/calendar/bulk-scheduling.state.service";
import { EmailSetupDialogComponent } from "app/main/pop-ups/email-setup-dialog/email-setup-dialog.component";
import { AppUpdateDialogComponent } from "app/main/plus/components/app-update-dialog/app-update-dialog.component";
import { EmailStateService } from 'app/core/services/email-state.service';

@Component({
  selector: "aep-layout",
  templateUrl: "./layout.component.html",
  styleUrls: ["./layout.component.scss"],
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger("slideInOut", [
      transition(":enter", [
        style({ transform: "translateY(-100%)" }),
        animate("300ms ease-in", style({ transform: "translateY(0%)" })),
      ]),
      transition(":leave", [
        animate("300ms ease-in", style({ transform: "translateY(-100%)" })),
      ]),
    ]),
  ],
  standalone: false,
})
export class LayoutComponent implements OnInit, OnDestroy {

  private _fusePerfectScrollbar: FusePerfectScrollbarDirective;

  @ViewChild(FusePerfectScrollbarDirective, { static: true })
  set directive(theDirective: FusePerfectScrollbarDirective) {
    if (!theDirective) {
      return;
    }

    this._fusePerfectScrollbar = theDirective;

  }

  innerWidth: number;

  fuseConfig: any;

  topNotConfig : any;

  navigation: any;

  notifactions: [];

  role: any;

  devicetype: any;

  printer: "par" | "aip" | "ppl" | "pp" | "p" = null;

  printerData;

  editingEvent = false;

  // Private
  private _unsubscribeAll: Subject<any>;

  private emailDialogOpened = false;
  public email_address: string = "";
  public showEmailBanner = false;

  private streamSubscription?: Subscription;
  currentEvents: any[] = [];
  isConnected = false;
  connectionError: string | null = null;


  /**
     * Constructor
     *
     * @param {FuseConfigService} _fuseConfigService
     */
     
  constructor(
    private globalService: GlobalService,
    private _fuseConfigService: FuseConfigService,
    private _printService: PrintService,
    private _topNotService: TopNotificationService,
    private _layoutService: LayoutService,
    private _popupDialogService: PopupDialogQueueService,    
    private _matDialog: MatDialog,
    private _viewPortService : ViewportService,
    private router:Router,
    private _eventStreamService: EventStreamService,
    private emailState: EmailStateService,
    public bulkSchedulingStateService: BulkSchedulingStateService
  ) {
    this.innerWidth = this._viewPortService.currentWidth;

    // Set the defaults
    this.navigation = [];

    // Set the private defaults
    this._unsubscribeAll = new Subject();
  }

  setNotToDefault(): void {
    this._topNotService.setToDefault();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

    

  scrollTo(offset): void {
    this._fusePerfectScrollbar.scrollToY(offset, 500);
  }

  /**
     * On init
     */
  ngOnInit(): void {
    this.connectToStream();
    this.viewPortListener()
    this._popupDialogService.subscribeToDialogState("EventFormComponent")
      .pipe(
        takeUntil(this._unsubscribeAll),
      )
      .subscribe((isOpen) => {
        const isCalendar = this.router.url.indexOf("calendar") !== -1;
        if (!isCalendar) return;
        console.log("EV FLOW");
        if (isOpen) {
          this.editingEvent = true;
        } else {
          this.editingEvent = false;
        } 
      });

    this._topNotService.config.subscribe(config=>{
      this.topNotConfig = config;
    });

    // Subscribe to global email state so the layout always reflects current email
    this.emailState.email$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(email => {
        this.email_address = email;
      });

    // Listen for explicit requests to show the email confirmation banner
    this.emailState.showBanner$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(show => {
        // only show banner if email exists and banner not dismissed
        if (show && this.email_address) {
          this.showEmailBanner = true;
        }
      });

    this.globalService.currentUser
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((user) => {
        if (!user) {
          this.showEmailBanner = false;
          return;
        }

        const needsEmailConfirm = !!user.email_address && user.email_confirmed === false;
        this.showEmailBanner = needsEmailConfirm;

        if (this.emailDialogOpened) return;

        const hasEmail = !!(user.email_address);
        this.emailState.setEmail(user.email_address || '');

            if (!hasEmail) {
              // Wait briefly to see if an AppUpdateDialog will be queued/opened (real-time may enqueue it after ~2s)
              if ((this as any)._emailDialogPending || this.emailDialogOpened) return;
              (this as any)._emailDialogPending = true;

                setTimeout(() => {
                  (this as any)._emailDialogPending = false;

                  // Check for any unopened app-update notifications first; if any exist, skip opening the email dialog
                  this._layoutService.fetchUnopenedAppUpdateNotifications().pipe(take(1)).subscribe((unopened) => {
                    if (unopened && unopened.data) {
                      return;
                    }
                    this.emailDialogOpened = true;
                    this._popupDialogService.openDialog(EmailSetupDialogComponent, {
                      panelClass: ["common-dialog-style"],
                      disableClose: false,
                      data: {
                        currentEmail: user.email_address,
                        title: "confirm_email.add_email_title",                      
                      },
                    });
                  });
                }, 2200);
            }
      });


    this._layoutService.scrollTo.subscribe({
      next:(offset)=>this.scrollTo(offset),
    });

    this._fuseConfigService.config
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((config) => {
        this.fuseConfig = config;
      });

    this._printService._print
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.printer = "p";
        this.printerData = data;
        setTimeout(() => {
          window.print();
        }, 1000);
      });

    this._printService._printAgencyRecu
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.printer = "par";
        this.printerData = data;
        setTimeout(() => {
          window.print();
        }, 1000);
      });

    this._printService._printAgencyInvoice
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.printer = "aip";
        this.printerData = data;
        setTimeout(() => {
          window.print();
        }, 1000);
      });

    this._printService._printPaiement
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.printer = "pp";
        this.printerData = data;
        setTimeout(() => {
          window.print();
        }, 1000);
      });

    this._printService._PrintPlanning
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((data) => {
        this.printer = "ppl";
        this.printerData = data;
        setTimeout(() => {
          window.print();
        }, 1000);
      });


    this.devicetype = this.globalService.detectdevice();
    //console.log(this.devicetype)
  }

  logui(): void {
    //event.stopPropagation()
  }

  openEmailConfirmation(): void {
    // Don't permanently dismiss the banner when opening the dialog;
    // keep it controlled by user dismiss action or email verification state.
    this._popupDialogService.openDialog(EmailSetupDialogComponent, {
      panelClass: ["common-dialog-style"],
      disableClose: false,
      data: {
        currentEmail: this.email_address,
        title: "confirm_email.title",
        showSteps: true,
      },
    });
  }

  dismissEmailBanner(): void {
    this.showEmailBanner = false;
  }

  viewPortListener(){
    this._viewPortService.innerWidth$?.pipe(takeUntil(this._unsubscribeAll)).subscribe(innerWidth => {
      this.innerWidth = innerWidth;
    });
  }

  /**
     * On destroy
     */
  ngOnDestroy(): void {
    this.disconnect();
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(true);
    this._unsubscribeAll.complete();
  }


  /**
   * Connect to the SSE stream
   */
  connectToStream(): void {
    console.log('Connecting to event stream...');
    
    this.streamSubscription = this._eventStreamService
      .connectToEventStream()
      .subscribe({
        next: (data) => {
          console.log('[SSE] Received data:', data);
          if (data.error) {
            this.connectionError = data?.error;
            this.currentEvents = [];
          } else {
            this.connectionError = null;
            const newEvents = data?.currentEvents || [];
            // Simple check: only update if different
            if (!this.areEventsEqual(this.currentEvents, newEvents)) {
              this.currentEvents = newEvents;
              console.log("this.currentEvents==>",this.currentEvents);
            }
            this.isConnected = true;
          }
        },
        error: (error) => {
          console.error('Stream error:', error);
          this.connectionError = 'Connection lost. Retrying...';
          this.isConnected = false;
          this.currentEvents = [];
          
          // Retry logic
          setTimeout(() => {
            if (!this._eventStreamService.isConnected()) {
              this.connectToStream();
            }
          }, 5000);
        },
        complete: () => {
          console.log('Stream completed');
          this.isConnected = false;
        }
      });
  }
  
  private areEventsEqual(current: any[], incoming: any[]): boolean {
    if (current.length !== incoming.length) return false;
    
    return current.every((event, index) => {
      const incomingEvent = incoming[index];
      return event.id === incomingEvent.id &&
            event.startTime === incomingEvent.startTime &&
            event.endTime === incomingEvent.endTime &&
            event.title === incomingEvent.title &&
            event.candidateName === incomingEvent.candidateName;
    });
  }
  /**
   * Disconnect from the stream
   */
  disconnect(): void {
    if (this.streamSubscription) {
      this.streamSubscription.unsubscribe();
    }
    this._eventStreamService.disconnect();
    this.isConnected = false;
  }

}
